/* eslint-disable react/display-name */
import React, { useMemo, useEffect, useState } from 'react'
// import MaterialTable from 'material-table'
import MaterialTableCore from '@material-table/core'
import { tableIcons } from './MaterialTableIcons'
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import { makeStyles } from '@material-ui/core'
import Cookies from 'js-cookie'
import { useAuth0 } from '@auth0/auth0-react'

const defaultOptions = {
  filtering: false,
  search: true,
  rowStyle: {
    fontSize: `14px`,
    textAlign: 'center',
    height: `30px`,
  },
  paging: false,
  addRowPosition: 'first',
  headerStyle: {
    backgroundColor: 'white',
    position: 'sticky',
    top: 0,
  },
  debounceInterval: 1000,
}

const useStyles = makeStyles(() => ({
  dateWrapper: {
    '& .MuiInputBase-input': {
      fontSize: 13,
    },
  },
}))

export const MTDateInputComponent = React.memo((props) => {
  const classes = useStyles()
  const { onChange, dateFormat = 'MM/dd/yyyy', value } = props

  return (
    <div className={classes.dateWrapper}>
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <DatePicker
          variant='normal'
          format={dateFormat}
          style={{ fontSize: '12px !important' }}
          value={value}
          onChange={(value) => onChange(value)}
        />
      </MuiPickersUtilsProvider>
    </div>
  )
})

const addPropsToColumns = (columnArr) => {
  for (const col of columnArr) {
    const { title, type, dateFormat = 'MM/dd/yyyy' } = col
    if (type === 'date') {
      col.editComponent = (props) => <MTDateInputComponent title={title} dateFormat={dateFormat} {...props} />
    }
  }
}
const exportToCsv = (columns, data, fileName = 'table-export') => {
  const delimiter = ','
  const exportableColumns = columns.filter((col) => !col.hidden && (col.field || col.title))

  const headers = exportableColumns.map((col) => `"${col.title || ''}"`).join(delimiter)
  const rows = data
    .map((row) =>
      exportableColumns
        .map((col) => {
          // Handle nested properties (e.g. 'tracking.carrier') if field contains dots
          const field = col.field
          let val = row[field]

          if (field && field.includes('.')) {
            val = field.split('.').reduce((obj, key) => (obj ? obj[key] : undefined), row)
          }

          const cellStr = String(val === null || val === undefined ? '' : val)
          const escaped = cellStr.replace(/"/g, '""')
          return `"${escaped}"`
        })
        .join(delimiter),
    )
    .join('\r\n')

  const csvContent = headers + '\r\n' + rows
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `${fileName}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

export default function MaterialTableCustom({
  columns: iColumns,
  title: iTitle,
  components: iComponents,
  options = {},
  rowStyle,
  cellStyle,
  headerStyle,
  data: iData,
  editable,
  parentChildData,
  queryData = undefined,
  actions = [], // Accept actions prop
}) {
  const { user } = useAuth0()

  // unique key per table (change to suit your needs)
  const safeEmail = (user?.email || 'guest').replace(/[^\w]/g, '_')
  const safeTitle = (iTitle || 'table').replace(/[^\w]/g, '_')
  const tableKey = `mt_columns_${safeEmail}_${safeTitle}`

  // local columns state so we can toggle `hidden`
  const [columns, setColumns] = useState([])

  // load saved visibility from cookie on mount or when iColumns changes
  useEffect(() => {
    // shallow clone incoming columns so we don't mutate props
    const incoming = (iColumns || []).map((c) => ({ ...c }))
    addPropsToColumns(incoming)

    const saved = Cookies.get(tableKey)
    if (saved) {
      try {
        const savedVisibility = JSON.parse(saved) // { fieldOrId: true/false } -> true = visible
        const updated = incoming.map((col) => {
          const key = col.field ?? col.tableData?.id ?? col.title
          if (key && Object.prototype.hasOwnProperty.call(savedVisibility, key)) {
            // invert here: savedVisibility true means visible => hidden = !visible
            return { ...col, hidden: !savedVisibility[key] }
          }
          return col
        })
        setColumns(updated)
        return
      } catch (e) {
        console.error('Failed to parse column visibility cookie', e)
        // ignore parse error and fall through to default
      }
    }

    // no saved cookie -> just use incoming as-is
    setColumns(incoming)
  }, [iColumns, tableKey])

  const handleChangeColumnHidden = (columnId, hidden) => {
    // update local columns state
    const newCols = columns.map((col) => {
      const key = col.field ?? col.tableData?.id ?? col.title
      if (key === columnId?.field) {
        return { ...col, hidden } // set hidden directly
      }
      return col
    })

    // build visibility map to save in cookie
    const visibilityMap = {}
    newCols.forEach((col) => {
      const key = col.field ?? col.tableData?.id ?? col.title
      if (key) visibilityMap[key] = !col.hidden // true = visible
    })

    Cookies.set(tableKey, JSON.stringify(visibilityMap), { expires: 30 })

    // update local state to trigger re-render
    setColumns(newCols)
  }

  const combinedOptions = {
    ...defaultOptions,
    ...options,
    columnsButton: true,
    // exportButton: true, // Removed standard export
  }

  if (rowStyle) combinedOptions.rowStyle = { ...combinedOptions.rowStyle, ...rowStyle }
  if (rowStyle) combinedOptions.headerStyle = { ...combinedOptions.headerStyle, ...headerStyle }
  if (cellStyle) combinedOptions.cellStyle = { ...combinedOptions.cellStyle, ...cellStyle }

  //addPropsToColumns(iColumns)
  //const columns = useMemo(() => iColumns, [iColumns])
  const title = useMemo(() => iTitle, [iTitle])
  const components = useMemo(() => iComponents, [iComponents])
  const data = useMemo(() => iData, [iData])

  if (!columns.length || !data.length) return <></>

  // Custom action for CSV Export
  const exportAction = {
    icon: () => <tableIcons.Export />,
    tooltip: 'Export CSV',
    position: 'toolbar',
    onClick: () => exportToCsv(columns, data, `${safeTitle}-export`),
  }

  // Prepend or append based on desired position (usually actions are right-aligned, so prepending might put it to the "left" of others)
  const combinedActions = [exportAction, ...(actions || [])]

  return (
    <MaterialTableCore
      title={title}
      components={components}
      icons={tableIcons}
      columns={columns}
      data={
        queryData
          ? async (query) => {
            return { data: await queryData(query.search) }
          }
          : data || []
      }
      options={combinedOptions}
      {...((editable) ? { editable: { ...editable } } : {})} // Fix editable spreading
      parentChildData={parentChildData}
      onChangeColumnHidden={handleChangeColumnHidden}
      actions={combinedActions}
    />
  )
}
